import { CSSProperties, ReactNode, useMemo } from 'react';

import { useMediaQuery } from 'usehooks-ts';

import { Typography } from '../Typography';
import { TableBody } from './TableBody';
import { TableBodyCellProps } from './TableBodyCell';
import { TableHeader } from './TableHeader';
import { calculateColumns } from './utils/calculateColumns';
import { generateLoadingData } from './utils/generateLoadingData';

export type TableData = {
  [key: string]: any;
};

type ColumnMeta<Data> = {
  name: keyof Data;
  label: string;
  hidden?: boolean;
};

export type TopColumnsType<Data> = Array<keyof Data>;

export interface TableColumn<Data> extends ColumnMeta<Data>, TableBodyCellProps {
  /**
   * Custom render function for the column cell
   * If not provided, the default value from `row[name]` will be used
   */
  render?: (
    params: {
      row: Data;
      rowIndex: number;
      colIndex: number;
    } & ColumnMeta<Data>,
  ) => ReactNode;
}

export interface TableProps<Data extends TableData> {
  /**
   * Array of column definitions
   * @default []
   */
  columns?: TableColumn<Data>[];
  /**
   * Array of data rows
   * @default []
   */
  data?: Data[];
  /**
   * Component to render when there is no data
   */
  emptyStateComponent?: ReactNode;
  /**
   * Custom grid template for desktop view
   */
  gridTemplateColumns?: CSSProperties['gridTemplateColumns'];
  /**
   * Array of column names to be shown at the top in mobile layout
   * @default column[0].name
   */
  topColumns?: TopColumnsType<Data>;
  /**
   * If `true`, the `Table` will display a skeleton loader
   * @default false
   */
  isLoading?: boolean;
}

const DEFAULT_LOADING_ROWS_COUNT = 3;
const DEFAULT_ROW_HEIGHT = 56;
const DEFAULT_EMPTY_STATE_ROWS_HEIGHT = DEFAULT_LOADING_ROWS_COUNT * DEFAULT_ROW_HEIGHT;

export const Table = <Data extends TableData>({
  columns = [] as TableColumn<Data>[],
  data = [],
  emptyStateComponent,
  gridTemplateColumns,
  topColumns,
  isLoading = false,
}: TableProps<Data>) => {
  const tableData = isLoading ? generateLoadingData(columns, DEFAULT_LOADING_ROWS_COUNT) : data;
  const hasData = tableData.length > 0;
  const isMobileLayout = useMediaQuery('(max-width: 1023px)');

  const { bodyColumns, headerColumns, columnCount, finalTopColumns } = useMemo(
    () => calculateColumns(columns, isMobileLayout, topColumns),
    [columns, topColumns, isMobileLayout],
  );

  const desktopTemplate = useMemo(
    () => gridTemplateColumns ?? `2fr repeat(${columnCount - 1}, 1fr)`,
    [columnCount, gridTemplateColumns],
  );

  const gridStyle = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: isMobileLayout
        ? `repeat(${headerColumns.length}, 1fr)`
        : desktopTemplate,
    }),
    [isMobileLayout, headerColumns.length, desktopTemplate],
  );

  return (
    <div className="w-full h-full overflow-auto font-primary text-[14px] leading-[20px] font-medium">
      <TableHeader columns={headerColumns} style={gridStyle} />
      {hasData ? (
        <TableBody
          columns={bodyColumns}
          data={tableData}
          style={gridStyle}
          isMobileLayout={isMobileLayout}
          topColumns={finalTopColumns}
          isLoading={isLoading}
        />
      ) : (
        <div
          className="flex items-center justify-center h-full"
          style={{
            height: DEFAULT_EMPTY_STATE_ROWS_HEIGHT,
          }}
        >
          {emptyStateComponent ?? (
            <Typography variant="caption-s" color="tertiary">
              No Results
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};
